import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import useAuth from 'app/hooks/useAuth'
import { hasFiscalPermission } from 'app/constants/fiscalPermissions'
import { fiscalApi, fiscalError } from 'app/views/money/fiscal/api'
import FiscalOrderDialog from './FiscalOrderDialog'

const Context=createContext({ openOrder:()=>{}, config:null, revision:0, refreshConfig:()=>Promise.resolve() })
export const useFiscal=()=>useContext(Context)

export default function FiscalProvider({children}) {
    const {user}=useAuth()
    const allowed=hasFiscalPermission(user,'FISCAL_VIEW') || (user?.accessPage || []).includes('CASHBOX_SHIFT_CLOSE')
    const account=String(user?.MainId || user?.id || '')
    const [config,setConfig]=useState(null)
    const [order,setOrder]=useState(null)
    const [revision,setRevision]=useState(0)
    useEffect(()=>{
        let active=true
        setConfig(null);setOrder(null)
        if (allowed) fiscalApi('bootstrap').then((data)=>{if(active)setConfig(data)}).catch((error)=>{if(active)setConfig({schema_ready:false,message:fiscalError(error)})})
        return ()=>{active=false}
    },[account,allowed])
    const openOrder=useCallback((id,environment='prod')=>setOrder({id:String(id),environment}),[])
    const changed=useCallback(()=>setRevision((value)=>value+1),[])
    const refreshConfig=useCallback(()=>fiscalApi('bootstrap').then(setConfig),[])
    const value=useMemo(()=>({openOrder,config,revision,refreshConfig}),[openOrder,config,revision,refreshConfig])
    return <Context.Provider value={value}>{children}{order && <FiscalOrderDialog key={`${account}:${order.id}:${order.environment}`} account={account} orderId={order.id} environment={order.environment} onEnvironmentChange={(environment)=>setOrder((old)=>({...old,environment}))} onClose={()=>setOrder(null)} onChanged={changed} onConfig={setConfig} />}</Context.Provider>
}

export function useFiscalOrderStatuses(orders, refresh) {
    const {config,revision}=useFiscal()
    const [states,setStates]=useState({})
    const idsKey=(orders || []).map((row)=>String(row.id)).sort().join(',')
    useEffect(()=>{
        let active=true
        if (!config?.schema_ready || !(config.permissions || []).includes('FISCAL_VIEW') || !idsKey) {setStates({});return undefined}
        const ids=idsKey.split(',')
        const chunks=[]
        for(let i=0;i<ids.length;i+=100) chunks.push(ids.slice(i,i+100))
        let loading=false
        const load=()=>{
            if(loading || document.hidden)return
            loading=true
            Promise.all(chunks.map((batch)=>fiscalApi('order-statuses',{ids:batch,environment:'prod'}))).then((results)=>{
                if(active) setStates(Object.fromEntries(results.flatMap((data)=>data.rows || []).map((row)=>[String(row.order_id),row])))
            }).catch(()=>{if(active)setStates(previous=>Object.fromEntries(ids.map(id=>[id,{...previous[id],state:'unavailable'}])))})
                .finally(()=>{loading=false})
        }
        load()
        const timer=setInterval(load,10000)
        document.addEventListener('visibilitychange',load)
        return ()=>{active=false;clearInterval(timer);document.removeEventListener('visibilitychange',load)}
    },[config,idsKey,revision,refresh])
    return states
}
